import { useEffect, useRef } from 'react';

export type KeyboardShortcutCallback = (event: KeyboardEvent) => void;

export type ParsedShortcut = {
  meta: boolean;
  shift: boolean;
  alt: boolean;
  key: string;
};

export function parseShortcut(spec: string): ParsedShortcut {
  const tokens = spec
    .toLowerCase()
    .split('+')
    .map((t) => t.trim());
  const parsed: ParsedShortcut = { meta: false, shift: false, alt: false, key: '' };
  for (const token of tokens) {
    if (token === 'meta' || token === 'cmd' || token === 'ctrl') {
      parsed.meta = true;
    } else if (token === 'shift') {
      parsed.shift = true;
    } else if (token === 'alt' || token === 'option') {
      parsed.alt = true;
    } else {
      parsed.key = token;
    }
  }

  return parsed;
}

/**
 * Render a shortcut spec for display in a UI hint (kbd chip, tooltip, etc).
 * Mac users see glyph modifiers (⌘/⌥/⇧); Windows/Linux see word modifiers
 * (Ctrl/Alt/Shift). Letter keys are uppercased.
 */
export function formatShortcut(spec: string, mac: boolean): string {
  const parsed = parseShortcut(spec);
  const parts: string[] = [];

  if (parsed.meta) {
    parts.push(mac ? '⌘' : 'Ctrl');
  }

  if (parsed.alt) {
    parts.push(mac ? '⌥' : 'Alt');
  }

  if (parsed.shift) {
    parts.push(mac ? '⇧' : 'Shift');
  }

  parts.push(parsed.key === 'click' ? 'Click' : parsed.key.toUpperCase());

  return parts.join('+');
}

/**
 * Listens for a keyboard shortcut at the document level. When the shortcut
 * matches, calls `event.preventDefault()` and invokes the callback.
 *
 * `spec` uses a small DSL: `"meta+b"`, `"meta+shift+k"`, etc. `meta` matches
 * either `metaKey` (Mac) or `ctrlKey` (Windows/Linux), so a single spec works
 * on both platforms.
 */
export function useKeyboardShortcut(spec: string, callback: KeyboardShortcutCallback): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const parsed = parseShortcut(spec);

    function onKeyDown(event: KeyboardEvent) {
      const metaPressed = event.metaKey || event.ctrlKey;
      if (parsed.meta !== metaPressed) {
        return;
      }

      if (parsed.shift !== event.shiftKey) {
        return;
      }

      if (parsed.alt !== event.altKey) {
        return;
      }

      if (event.key.toLowerCase() !== parsed.key) {
        return;
      }

      event.preventDefault();
      callbackRef.current(event);
    }

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [spec]);
}
